import { FC, useCallback } from 'react';
import { useSubmitTaskFeedback } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { taskCenterTrackingIds } from '@frontend/task-center-components';
import {
  Text,
  Button,
  useModalControl,
  ConfirmationModal,
  TextareaField,
  useFormField,
  useAlert,
} from '@frontend/design-system';
import { containerStyle, textStyle, feedbackButtonLoadingStyle } from './ai-task-tag.styles';

interface AiTaskTagProps {
  show?: boolean;
  locationId?: string;
  taskId?: string;
}
const MAX_FEEDBACK_LENGTH = 250;

export const AiTaskTag: FC<AiTaskTagProps> = ({ show, locationId, taskId }) => {
  const { t } = useTranslation('task-center');
  const alert = useAlert();
  const { modalProps, triggerProps } = useModalControl();
  const { mutateAsync, isLoading } = useSubmitTaskFeedback();
  const fieldProps = useFormField({ type: 'text' });

  const onClickThumbsUp = useCallback(async () => {
    if (!locationId || !taskId) return;
    try {
      await mutateAsync({ rating: 5, locationId, taskId });
      alert.success(t('Thank you for your feedback!'));
    } catch (error) {
      alert.error(t('Something went wrong. Please try again.'));
    }
  }, [locationId, taskId, mutateAsync, alert, t]);

  const onClickThumbsDown = useCallback(async () => {
    if (!locationId || !taskId) return;
    try {
      await mutateAsync({ rating: 1, locationId, taskId, comments: fieldProps.value.trim() });
      alert.success(t('Thank you for your feedback!'));
    } catch (error) {
      alert.error(t('Something went wrong. Please try again.'));
    }
  }, [alert, fieldProps.value, locationId, mutateAsync, t, taskId]);

  if (show) {
    return (
      <span css={containerStyle}>
        <Icon name='ai-draft' size={16} />
        <Text size='medium' color='subdued' css={textStyle}>
          {t('Title and description are generated by AI.')}
        </Text>
        <Button
          iconName='feedback'
          size='small'
          variant='secondary'
          trackingId={taskCenterTrackingIds.submitAiPositiveFeedbackButton}
          disabled={!locationId || !taskId}
          onClick={onClickThumbsUp}
          css={feedbackButtonLoadingStyle(isLoading && !modalProps.show)}
          loading={isLoading && !modalProps.show}
        />
        <Button
          iconName='feedback-bad'
          size='small'
          variant='secondary'
          trackingId={taskCenterTrackingIds.submitAiNegativeFeedbackModalOpenButton}
          disabled={!locationId || !taskId}
          {...triggerProps}
        />
        <ConfirmationModal
          onConfirm={onClickThumbsDown}
          title={t('Help improve AI-generated Title and Description')}
          message={t(
            'Tell us what felt off about the title or description. Your feedback helps us improve accuracy and relevance.'
          )}
          confirmLabel={t('Submit')}
          cancelTrackingId={taskCenterTrackingIds.submitAiNegativeFeedbackModalCancelButton}
          confirmTrackingId={taskCenterTrackingIds.submitAiNegativeFeedbackModalConfirmButton}
          backTrackingId={taskCenterTrackingIds.submitAiNegativeFeedbackModalCloseButton}
          {...modalProps}
        >
          <TextareaField
            label={t('Feedback (Optional)')}
            name='feedback-description'
            maxLength={MAX_FEEDBACK_LENGTH}
            {...fieldProps}
          />
          <Text size='medium' color='subdued' textAlign='right'>
            {fieldProps.value.length}/{MAX_FEEDBACK_LENGTH}
          </Text>
        </ConfirmationModal>
      </span>
    );
  }

  return null;
};
